import { Html } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import {
	type PointerEvent as ReactPointerEvent,
	type RefObject,
	useEffect,
	useRef,
	useState,
} from "react";
import type * as THREE from "three";
import type { WallOpening } from "../../domain/framing/generateFramedWall";
import { resolveWallHeightM } from "../../domain/geometry/buildingDimensions";
import { toLocalXZ } from "../../domain/viewer/saunaRotation";
import type { BuildInput, VisibilityFlags } from "../../engines/types";
import { getT } from "../../i18n";
import { emitter } from "../../lib/eventBus";
import { OpeningDragController } from "./OpeningDragController";
import { PartitionDragController } from "./PartitionDragController";
import type { OrbitControlsRef } from "./SceneControls";
import type { PartitionMeasurementPreview } from "./viewerMeasurements";

const WALL_BASE_Y = 0.165;
const POLL_MS = 200;

interface DragAffordancesProps {
	buildInput: BuildInput;
	visibility: VisibilityFlags;
	active?: boolean;
	controlsRef?: RefObject<OrbitControlsRef | null>;
	/** Whole-building orientation; the occlusion test is in model space. */
	rotationDeg?: number;
	/** The rotated rig — ghosts and pick planes are parented to it. */
	rigRef?: RefObject<THREE.Object3D | null>;
	onPartitionSplitChange?: (partitionSplit: number) => void;
	onPartitionDragPreviewChange?: (
		preview: PartitionMeasurementPreview | null,
	) => void;
	onOpeningMoved?: (opening: WallOpening) => void;
}

interface CameraSample {
	x: number;
	z: number;
	insideSauna: boolean;
}

/**
 * Where the camera stands **in the building's own frame**. Every occlusion
 * test below compares against ±W/2 / ±L/2, which are model coordinates — so
 * once the building can be rotated the camera has to be brought into the same
 * frame or "am I outside the left wall?" starts answering for the wrong wall.
 */
function sampleCamera(
	camera: THREE.Camera,
	buildInput: BuildInput,
	rotationDeg: number,
): CameraSample {
	const halfW = buildInput.dimensions.width / 2;
	const halfL = buildInput.dimensions.length / 2;
	const local = toLocalXZ(camera.position.x, camera.position.z, rotationDeg);
	const insideSauna =
		Math.abs(local.x) < halfW &&
		Math.abs(local.z) < halfL &&
		camera.position.y > 0 &&
		camera.position.y <
			WALL_BASE_Y + resolveWallHeightM(buildInput.wallHeightM);
	return { x: local.x, z: local.z, insideSauna };
}

function useCameraSample(
	buildInput: BuildInput,
	rotationDeg: number,
): CameraSample {
	const camera = useThree((s) => s.camera);
	const [sample, setSample] = useState(() =>
		sampleCamera(camera, buildInput, rotationDeg),
	);

	useEffect(() => {
		const update = () =>
			setSample(sampleCamera(camera, buildInput, rotationDeg));
		update();
		const id = setInterval(update, POLL_MS);
		return () => clearInterval(id);
	}, [camera, buildInput, rotationDeg]);

	return sample;
}

function isOpeningOccluded(
	opening: WallOpening,
	visibility: VisibilityFlags,
	sample: CameraSample,
	buildInput: BuildInput,
): boolean {
	if (opening.wall === "front" && !visibility.frontWall) return true;
	if (sample.insideSauna) return false;

	const shellOn = visibility.exterior && visibility.roof;
	const W = buildInput.dimensions.width;
	const L = buildInput.dimensions.length;

	if (opening.wall === "divider") {
		const dividerZ = -L / 2 + L * (buildInput.partitionSplit ?? 0.5);
		if (sample.z > dividerZ) {
			return visibility.frontWall && shellOn;
		}
		return shellOn;
	}
	if (opening.wall === "front") {
		return sample.z < L / 2 && shellOn;
	}
	if (opening.wall === "back") {
		return sample.z > -L / 2 && visibility.frontWall && shellOn;
	}
	if (opening.wall === "left") {
		return sample.x > -W / 2 && shellOn;
	}
	return sample.x < W / 2 && shellOn;
}

function getOpeningCenter(
	buildInput: BuildInput,
	opening: WallOpening,
): [number, number, number] {
	const W = buildInput.dimensions.width;
	const L = buildInput.dimensions.length;
	const split = buildInput.partitionSplit ?? 0.5;
	const dividerZ = -L / 2 + L * split;
	const widthM = opening.widthMm / 1000;
	const heightM = opening.heightMm / 1000;
	const yM = WALL_BASE_Y + opening.yMm / 1000 + heightM / 2;

	if (opening.wall === "front") {
		return [-W / 2 + opening.xMm / 1000 + widthM / 2, yM, L / 2 - 0.05];
	}
	if (opening.wall === "back") {
		return [-W / 2 + opening.xMm / 1000 + widthM / 2, yM, -L / 2 + 0.05];
	}
	if (opening.wall === "left") {
		return [-W / 2 + 0.05, yM, -L / 2 + opening.xMm / 1000 + widthM / 2];
	}
	if (opening.wall === "right") {
		return [W / 2 - 0.05, yM, -L / 2 + opening.xMm / 1000 + widthM / 2];
	}
	return [-W / 2 + opening.xMm / 1000 + widthM / 2, yM, dividerZ];
}

interface PartitionAffordanceProps {
	buildInput: BuildInput;
	visibility: VisibilityFlags;
	sample: CameraSample;
	controlsRef?: RefObject<OrbitControlsRef | null>;
	rigRef?: RefObject<THREE.Object3D | null>;
	onPartitionSplitChange?: (partitionSplit: number) => void;
	onPartitionDragPreviewChange?: (
		preview: PartitionMeasurementPreview | null,
	) => void;
}

function PartitionAffordance({
	buildInput,
	visibility,
	sample,
	controlsRef,
	rigRef,
	onPartitionSplitChange,
	onPartitionDragPreviewChange,
}: PartitionAffordanceProps) {
	const scene = useThree((s) => s.scene);
	const camera = useThree((s) => s.camera);
	const gl = useThree((s) => s.gl);
	const invalidate = useThree((s) => s.invalidate);
	const controllerRef = useRef<PartitionDragController | null>(null);

	// Tear down an in-flight drag if the handle unmounts mid-gesture
	// (e.g. the relax room is toggled off while dragging) and release the
	// bus flag so the measurement controller doesn't stay parked.
	useEffect(
		() => () => {
			if (controllerRef.current) {
				controllerRef.current.dispose();
				controllerRef.current = null;
				emitter.emit("partition:handleDrag", false);
			}
		},
		[],
	);

	const interactive = typeof onPartitionSplitChange === "function";

	const startPartitionDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
		if (!interactive || controllerRef.current) {
			return;
		}
		event.stopPropagation();

		const canvas = gl.domElement;
		const { pointerId } = event;
		// The gesture starts on this HTML handle but the controller binds
		// its move/up listeners to the canvas. Capture the pointer on the
		// canvas so every later event (including release over the handle)
		// reaches the controller and the drag can't get stuck.
		try {
			canvas.setPointerCapture(pointerId);
		} catch {
			// jsdom / unsupported — drag still works via canvas listeners.
		}
		if (controlsRef?.current) {
			controlsRef.current.enabled = false;
		}
		// Park the measurement controller for the duration of the gesture.
		emitter.emit("partition:handleDrag", true);

		const previousSplit = buildInput.partitionSplit ?? 0.5;
		const finish = () => {
			try {
				canvas.releasePointerCapture(pointerId);
			} catch {
				// already released
			}
			if (controlsRef?.current) {
				controlsRef.current.enabled = true;
			}
			controllerRef.current = null;
			emitter.emit("partition:handleDrag", false);
			onPartitionDragPreviewChange?.(null);
			invalidate();
		};

		const controller = new PartitionDragController({
			scene,
			parent: rigRef?.current ?? undefined,
			camera: camera as THREE.PerspectiveCamera,
			renderer: gl,
			dimensions: buildInput.dimensions,
			partitionSplit: previousSplit,
			heaterType: buildInput.thermalInputs?.heaterType,
			heaterModelId: buildInput.thermalInputs?.heaterModelId,
			wallHeightM: buildInput.wallHeightM,
			onCommit: (nextSplit) => {
				// A plain click (no movement) commits the same value — skip
				// it so it doesn't add a no-op undo entry.
				if (nextSplit !== previousSplit) {
					onPartitionSplitChange?.(nextSplit);
				}
				finish();
			},
			onCancel: finish,
			onDragUpdate: () => invalidate(),
			onPreviewChange: onPartitionDragPreviewChange,
		});
		controller.startDrag();
		controllerRef.current = controller;
		invalidate();
	};

	if (typeof buildInput.partitionSplit !== "number") return null;
	if (buildInput.hasRelaxRoom === false) return null;

	const shellOn = visibility.exterior && visibility.roof;
	if (shellOn && !sample.insideSauna) return null;

	const t = getT(buildInput.market ?? "se");
	const split = buildInput.partitionSplit;
	const length = buildInput.dimensions.length;
	const halfWidth = buildInput.dimensions.width / 2;
	const dividerZ = -length / 2 + length * split;
	const HANDLE_INSET = 0.18;
	const HANDLE_X = -halfWidth + HANDLE_INSET;
	const HANDLE_Y =
		WALL_BASE_Y + resolveWallHeightM(buildInput.wallHeightM) - 0.18;

	const handleContent = (
		<>
			<span className="viewer-drag-affordance__label">
				{t.viewer_measurementPartitionTitle}
			</span>
			<svg
				aria-hidden="true"
				focusable="false"
				width="12"
				height="16"
				viewBox="0 0 12 16"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path
					d="M6 6 L6 1 M6 1 L3 4 M6 1 L9 4"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M6 10 L6 15 M6 15 L3 12 M6 15 L9 12"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</svg>
		</>
	);

	return (
		<Html
			position={[HANDLE_X, HANDLE_Y, dividerZ]}
			center
			transform={false}
			pointerEvents={interactive ? "auto" : "none"}
			zIndexRange={[40, 0]}
		>
			{interactive ? (
				<button
					type="button"
					className="viewer-drag-affordance viewer-drag-affordance--partition"
					aria-label={t.viewer_measurementPartitionTitle}
					onPointerDown={startPartitionDrag}
				>
					{handleContent}
				</button>
			) : (
				<div
					className="viewer-drag-affordance viewer-drag-affordance--partition"
					aria-hidden="true"
				>
					{handleContent}
				</div>
			)}
		</Html>
	);
}

interface OpeningChevronProps {
	buildInput: BuildInput;
	visibility: VisibilityFlags;
	opening: WallOpening;
	sample: CameraSample;
	controlsRef?: RefObject<OrbitControlsRef | null>;
	rigRef?: RefObject<THREE.Object3D | null>;
	onOpeningMoved?: (opening: WallOpening) => void;
}

const openingChevronIcon = (
	<svg
		aria-hidden="true"
		focusable="false"
		width="44"
		height="14"
		viewBox="0 0 44 14"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path
			d="M9 7 L3 7 M3 7 L7 3 M3 7 L7 11"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M35 7 L41 7 M41 7 L37 3 M41 7 L37 11"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<rect
			x="20"
			y="3"
			width="4"
			height="8"
			rx="1.5"
			fill="currentColor"
			opacity="0.85"
		/>
	</svg>
);

function OpeningChevron({
	buildInput,
	visibility,
	opening,
	sample,
	controlsRef,
	rigRef,
	onOpeningMoved,
}: OpeningChevronProps) {
	const scene = useThree((s) => s.scene);
	const camera = useThree((s) => s.camera);
	const gl = useThree((s) => s.gl);
	const invalidate = useThree((s) => s.invalidate);
	const controllerRef = useRef<OpeningDragController | null>(null);
	// The pointer id of the in-flight gesture, so the unmount-cleanup effect
	// (which has no access to the closure inside startOpeningDrag) can still
	// release capture and restore orbit controls if the pill unmounts
	// mid-drag (e.g. the opening is deleted from under the gesture).
	const activePointerIdRef = useRef<number | null>(null);

	// Tear down an in-flight drag if the pill unmounts mid-gesture (e.g. the
	// opening is deleted, or section view is toggled on, while dragging) and
	// release the bus flag so the measurement controller doesn't stay parked.
	useEffect(
		() => () => {
			if (controllerRef.current) {
				controllerRef.current.dispose();
				controllerRef.current = null;
				if (activePointerIdRef.current !== null) {
					try {
						gl.domElement.releasePointerCapture(activePointerIdRef.current);
					} catch {
						// already released
					}
					activePointerIdRef.current = null;
				}
				if (controlsRef?.current) {
					controlsRef.current.enabled = true;
				}
				emitter.emit("opening:handleDrag", false);
			}
		},
		[gl, controlsRef],
	);

	const interactive = typeof onOpeningMoved === "function";

	const startOpeningDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
		if (!interactive || controllerRef.current) {
			return;
		}
		event.stopPropagation();

		const canvas = gl.domElement;
		const { pointerId } = event;
		// Same choreography as the partition handle: the gesture starts on
		// this HTML button but the controller binds move/up listeners to the
		// canvas, so capture the pointer there or a release over the button
		// never reaches it and the drag gets stuck.
		try {
			canvas.setPointerCapture(pointerId);
		} catch {
			// jsdom / unsupported — drag still works via canvas listeners.
		}
		activePointerIdRef.current = pointerId;
		if (controlsRef?.current) {
			controlsRef.current.enabled = false;
		}
		// Park the measurement controller for the duration of the gesture.
		emitter.emit("opening:handleDrag", true);

		const finish = () => {
			try {
				canvas.releasePointerCapture(pointerId);
			} catch {
				// already released
			}
			activePointerIdRef.current = null;
			if (controlsRef?.current) {
				controlsRef.current.enabled = true;
			}
			// Commit/cancel inside OpeningDragController only tears down its
			// ghost mesh (cleanup()); the invisible raycast wall planes it
			// builds in buildWallPlanes() are only removed by dispose(). Without
			// this, every pill drag leaks up to 5 plane meshes into the scene.
			// dispose() re-runs cleanup() internally, which is idempotent, so
			// calling it here after an already-committed/cancelled gesture is
			// safe regardless of call order.
			controllerRef.current?.dispose();
			controllerRef.current = null;
			emitter.emit("opening:handleDrag", false);
			invalidate();
		};

		const controller = new OpeningDragController({
			scene,
			parent: rigRef?.current ?? undefined,
			camera: camera as THREE.PerspectiveCamera,
			renderer: gl,
			dimensions: buildInput.dimensions,
			existingOpenings: buildInput.openings,
			overrides: buildInput.overrides,
			partitionSplit: buildInput.partitionSplit,
			hasRelaxRoom:
				buildInput.hasRelaxRoom ?? buildInput.partitionSplit !== undefined,
			saunaPosition: buildInput.saunaPosition,
			roofConfig: buildInput.roofConfig,
			wallHeightM: buildInput.wallHeightM,
			onCommit: (nextOpening) => {
				onOpeningMoved?.(nextOpening);
				finish();
			},
			onCancel: finish,
			onDragUpdate: () => invalidate(),
		});
		controller.startMoveDrag(opening);
		controllerRef.current = controller;
		invalidate();
	};

	if (isOpeningOccluded(opening, visibility, sample, buildInput)) return null;

	const t = getT(buildInput.market ?? "se");
	const position = getOpeningCenter(buildInput, opening);
	const label =
		opening.type === "door"
			? t.viewer_measurementDoorTitle
			: t.viewer_measurementWindowTitle;

	return (
		<Html
			position={position}
			center
			transform={false}
			pointerEvents={interactive ? "auto" : "none"}
			zIndexRange={[40, 0]}
		>
			{interactive ? (
				<button
					type="button"
					className="viewer-drag-affordance viewer-drag-affordance--opening"
					aria-label={label}
					onPointerDown={startOpeningDrag}
				>
					<span className="viewer-drag-affordance__label">{label}</span>
					{openingChevronIcon}
				</button>
			) : (
				<div
					className="viewer-drag-affordance viewer-drag-affordance--opening"
					aria-hidden="true"
				>
					<span className="viewer-drag-affordance__label">{label}</span>
					{openingChevronIcon}
				</div>
			)}
		</Html>
	);
}

export function PartitionDragAffordance({
	buildInput,
	visibility,
	active = true,
	controlsRef,
	rotationDeg = 0,
	rigRef,
	onPartitionSplitChange,
	onPartitionDragPreviewChange,
	onOpeningMoved,
}: DragAffordancesProps) {
	const sample = useCameraSample(buildInput, rotationDeg);

	if (!active) return null;

	return (
		<>
			<PartitionAffordance
				buildInput={buildInput}
				visibility={visibility}
				sample={sample}
				controlsRef={controlsRef}
				rigRef={rigRef}
				onPartitionSplitChange={onPartitionSplitChange}
				onPartitionDragPreviewChange={onPartitionDragPreviewChange}
			/>
			{buildInput.openings.map((opening) => (
				<OpeningChevron
					key={opening.id}
					buildInput={buildInput}
					visibility={visibility}
					opening={opening}
					sample={sample}
					controlsRef={controlsRef}
					rigRef={rigRef}
					onOpeningMoved={onOpeningMoved}
				/>
			))}
		</>
	);
}
